// services/sessionService.ts

/**
 * Session Service
 *
 * Manages a persistent guest ID for tracking guest user sessions.
 * The guest ID is stored in localStorage and persists across tabs and page reloads.
 * The same UUID is reused until the user clears browser data.
 * This ID is sent as X-Guest-Id header and used for session heartbeats.
 *
 * Note: guest user creation in the database is deferred until the first chat
 * message (see ensureGuestUser middleware), so generating a UUID here does NOT
 * create a DB record — it just provides a stable client-side identifier.
 */

const GUEST_ID_KEY = "webapp_session_id";

/**
 * Generate a UUID v4 for guest identification
 */
function generateUUID(): string {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create a guest ID
 * Returns existing guest ID from localStorage or creates a new one
 */
export function getGuestId(): string {
  try {
    let guestId = localStorage.getItem(GUEST_ID_KEY);

    if (!guestId) {
      guestId = generateUUID();
      localStorage.setItem(GUEST_ID_KEY, guestId);
    }

    return guestId;
  } catch {
    // localStorage might be unavailable (private browsing, etc.)
    // Generate a new guest ID for this page session only
    return generateUUID();
  }
}

/**
 * Clear the guest ID (e.g., on logout or reset)
 */
export function clearGuestId(): void {
  try {
    localStorage.removeItem(GUEST_ID_KEY);
  } catch {
    // Ignore errors if localStorage is unavailable
  }
}

/**
 * Check if a guest ID exists in storage
 */
export function hasGuestId(): boolean {
  try {
    return localStorage.getItem(GUEST_ID_KEY) !== null;
  } catch {
    return false;
  }
}

// Legacy exports for backward compatibility
export const getSessionId = getGuestId;
export const clearSessionId = clearGuestId;
export const hasSessionId = hasGuestId;
